import React from 'react';
import { Controller, useFormContext, ControllerRenderProps, FieldValues } from 'react-hook-form';

// Base styles without border color
const inputStylesBase = 'p-[10px] text-[16px]/[20px] border-2 rounded-[4px] transition-colors';
const labelStyles = 'mt-[16px] block font-extrabold text-[12px]/[14px]';
const requiredStyles = 'text-[#F02D3A] text-[10px]/[14px] align-top';

// Helper to get border class based on whether field has a value
const getInputBorderClass = (value: string | null | undefined): string => {
  return value && String(value).trim() !== '' ? 'border-black' : 'border-gray-300';
};

interface FormInputProps {
  name: string;
  label?: string;
  required?: boolean;
  type?: 'text' | 'email' | 'tel' | 'password' | 'number' | 'date';
  placeholder?: string;
  className?: string;
  rules?: Record<string, unknown>;
  disabled?: boolean;
  maxLength?: number;
  max?: string;
  min?: string;
  // For custom onChange handling (like phone formatting)
  formatValue?: (value: string) => string;
}

/**
 * A form input that automatically applies dynamic border styling
 * based on whether the field has a value (dark border) or is empty (light border).
 * 
 * Supports text, email, tel, password, number, and date input types.
 * Uses react-hook-form's Controller under the hood.
 */
export const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  required = false,
  type = 'text',
  placeholder,
  className = '',
  rules = {},
  disabled = false,
  maxLength,
  max,
  min,
  formatValue,
}) => {
  const { control, formState: { errors } } = useFormContext();
  
  // Get nested error (supports paths like 'person.first_name')
  const getNestedError = (path: string) => {
    return path.split('.').reduce((acc: any, part) => acc?.[part], errors);
  };
  
  const error = getNestedError(name);
  
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<FieldValues, string>
  ) => {
    let value = e.target.value;
    if (formatValue) {
      value = formatValue(value);
    }
    field.onChange(value);
  };

  return (
    <div className={className}>
      {label && (
        <label htmlFor={name} className={labelStyles}>
          {label}
          {required && <span className={requiredStyles}>*</span>}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        rules={{ required: required ? 'This field is required' : false, ...rules }}
        render={({ field }) => (
          <input
            id={name}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            max={max}
            min={min}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => handleChange(e, field)}
            className={`${inputStylesBase} ${getInputBorderClass(field.value)} w-full`}
          />
        )}
      />
      {error && (
        <p className="mt-1 text-sm text-[#ff4b4b]">
          {typeof error.message === 'string' ? error.message : 'This field is required'}
        </p>
      )}
    </div>
  );
};

interface FormSelectProps {
  name: string;
  label?: string;
  required?: boolean;
  options: { value: string; label: string }[];
  className?: string;
  rules?: Record<string, unknown>;
  disabled?: boolean;
  defaultValue?: string;
}

/**
 * A native select that automatically applies dynamic border styling.
 */
export const FormSelect: React.FC<FormSelectProps> = ({
  name,
  label,
  required = false,
  options,
  className = '',
  rules = {},
  disabled = false,
  defaultValue = '',
}) => {
  const { control, formState: { errors } } = useFormContext();
  
  const getNestedError = (path: string) => {
    return path.split('.').reduce((acc: any, part) => acc?.[part], errors);
  };
  
  const error = getNestedError(name);

  return (
    <div className={className}>
      {label && (
        <label htmlFor={name} className={labelStyles}>
          {label}
          {required && <span className={requiredStyles}>*</span>}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={{ required: required ? 'This field is required' : false, ...rules }}
        render={({ field }) => (
          <select
            id={name}
            disabled={disabled}
            {...field}
            value={field.value ?? defaultValue}
            className={`${inputStylesBase} ${field.value ? 'border-black' : 'border-gray-300'} w-full bg-transparent`}
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
      />
      {error && (
        <p className="mt-1 text-sm text-[#ff4b4b]">
          {typeof error.message === 'string' ? error.message : 'This field is required'}
        </p>
      )}
    </div>
  );
};

interface FormCheckboxProps {
  name: string;
  label: string;
  className?: string;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}

/**
 * A checkbox input with label.
 */
export const FormCheckbox: React.FC<FormCheckboxProps> = ({
  name,
  label,
  className = '',
  disabled = false,
  onChange,
}) => {
  const { control } = useFormContext();

  return (
    <div className={`flex items-center ${className}`}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <input
            id={name}
            type="checkbox"
            disabled={disabled}
            checked={field.value || false}
            onChange={(e) => {
              field.onChange(e.target.checked);
              onChange?.(e.target.checked);
            }}
            className="mr-2 h-4 w-4 cursor-pointer"
          />
        )}
      />
      <label htmlFor={name} className="cursor-pointer text-[14px]/[18px] font-medium">
        {label}
      </label>
    </div>
  );
};

// Phone number formatter helper
export const formatPhoneNumber = (value: string): string => {
  let digits = value.replace(/\D/g, '');
  // Drop a leading US country code so 11-digit inputs (e.g. pasted "1-555-…")
  // format against the right 10 digits instead of being silently truncated.
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  let formatted = '';
  if (digits.length >= 1) {
    formatted = `(${digits.slice(0, 3)}`;
  }
  if (digits.length >= 4) {
    formatted += `) ${digits.slice(3, 6)}`;
  }
  if (digits.length >= 7) {
    formatted += `-${digits.slice(6, 10)}`;
  }
  return formatted;
};

/**
 * Normalize a stored phone for display/validation. Returns a fully formatted
 * (XXX) XXX-XXXX string when the value resolves to exactly 10 digits (optionally
 * with a leading US country code); otherwise returns the original value
 * UNCHANGED — so a non-standard number from a Web Campaign Request is never
 * partially mangled into an invalid string (which would block re-saving the
 * Client Info form, HHLTB-2343). Use this for pre-filling existing values;
 * use formatPhoneNumber for progressive formatting while the user types.
 */
export const normalizePhone = (value: string): string => {
  if (!value) return value;
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  return value;
};

// Export styles for cases where manual styling is needed (e.g., field arrays)
export { inputStylesBase, getInputBorderClass, labelStyles, requiredStyles };

export default FormInput;
